import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { API } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  FileText, Download, Table2, Printer, Sparkles, X, 
  Users, Send, Loader2, ChevronRight, ZoomIn, ZoomOut,
  Filter, Check, AlertTriangle, HelpCircle
} from "lucide-react";
import DatabaseQualityDashboard from "@/components/DatabaseQualityDashboard";
import PreAnalysisQuestionnaire from "@/components/PreAnalysisQuestionnaire";

// ═══════════════════════════════════════════════════════════════════════════════
// BLUEPRINT TAKEOFF PAGE - SPEC OPS UI
// ═══════════════════════════════════════════════════════════════════════════════

const BlueprintTakeoffPage = () => {
  const navigate = useNavigate();
  
  // ═══════════════════════════════════════════════════════════════════════════
  // STATE
  // ═══════════════════════════════════════════════════════════════════════════
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [materials, setMaterials] = useState([]);
  const [blueprintUrl, setBlueprintUrl] = useState(null);
  const [pdfScale, setPdfScale] = useState(1);
  const [showDevTools, setShowDevTools] = useState(true); // Database Quality Dashboard
  
  // Pre-Analysis Questionnaire State
  const [showQuestionnaire, setShowQuestionnaire] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [pendingFilename, setPendingFilename] = useState("");
  const [questionnaireAnswers, setQuestionnaireAnswers] = useState(null);
  
  // AI Command Panel
  const [aiPanelOpen, setAiPanelOpen] = useState(false);
  const [chatHistory, setChatHistory] = useState([
    {
      role: "assistant",
      content: "**Blueprint Analysis Ready**\n\nUpload a blueprint PDF to begin. The system will:\n\n• Analyze the blueprint and extract materials\n• Match items to the master catalog (1,265+ items)\n• Apply current pricing automatically\n• Flag items that need manual review\n\nStanding by for upload...",
      timestamp: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
    }
  ]);
  const [chatInput, setChatInput] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  
  // Statistics
  const [statistics, setStatistics] = useState(null);
  
  // Filter
  const [activeFilter, setActiveFilter] = useState("all"); // all, matched, review
  
  // Target selection
  const [selectedTarget, setSelectedTarget] = useState("");
  const [targets, setTargets] = useState([]);
  const [loadingTargets, setLoadingTargets] = useState(true);
  const [generatingEstimate, setGeneratingEstimate] = useState(false);
  
  // Refs
  const fileInputRef = useRef(null);
  const chatContainerRef = useRef(null);
  
  // ═══════════════════════════════════════════════════════════════════════════
  // EFFECTS
  // ═══════════════════════════════════════════════════════════════════════════
  
  // Fetch leads and clients for target selector
  useEffect(() => {
    const fetchTargets = async () => {
      setLoadingTargets(true);
      try {
        const [leadsRes, clientsRes] = await Promise.all([
          API.get("/leads"),
          API.get("/clients")
        ]);
        
        const leads = (leadsRes.data || []).map(l => ({
          id: l.lead_id || l.id,
          name: l.name || l.contact_name || "Unknown Lead",
          type: "lead",
        }));
        
        const clients = (clientsRes.data || []).map(c => ({
          id: c.client_id || c.id,
          name: c.name || "Unknown Client",
          type: "client"
        }));
        
        setTargets([...leads, ...clients].sort((a, b) => a.name.localeCompare(b.name)));
      } catch (error) {
        console.error("Failed to fetch targets:", error);
      } finally {
        setLoadingTargets(false);
      }
    };
    fetchTargets();
  }, []);
  
  // Auto-scroll chat
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [chatHistory]);
  
  // Keyboard shortcut: Ctrl+Shift+D to toggle dev tools
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'D') {
        e.preventDefault();
        setShowDevTools(prev => !prev);
      }
    };
    
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, []);
  
  // Track gaps when materials are loaded
  const trackGaps = useCallback(async (matchedMaterials) => {
    try {
      const gapsToTrack = matchedMaterials
        .filter(m => m.match_score < 0.90)
        .map(m => ({
          item_name: m.item_name || m.ai_item_name,
          quantity: m.quantity || 0,
          unit: m.unit || 'EA',
          match_score: m.match_score || 0,
          matched_to: m.match_score > 0 ? m.item_name : '',
          category: m.category || ''
        }));
      
      if (gapsToTrack.length > 0) {
        await API.post('/blueprint-takeoff/gaps/track', {
          items: gapsToTrack,
          blueprint_id: `bp_${Date.now()}`,
          project_name: 'Blueprint Analysis'
        });
        console.log(`Tracked ${gapsToTrack.length} database gaps`);
      }
    } catch (err) {
      console.error('Failed to track gaps:', err);
    }
  }, []);
  
  // ═══════════════════════════════════════════════════════════════════════════
  // FILE UPLOAD & ANALYSIS
  // ═══════════════════════════════════════════════════════════════════════════
  
  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };
  
  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Please upload a PDF blueprint");
      e.target.value = "";
      return;
    }
    
    // Display blueprint immediately
    const objectUrl = URL.createObjectURL(file);
    setBlueprintUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return objectUrl;
    });
    
    // Open AI panel 
    setAiPanelOpen(true);
    
    // Add upload message to chat
    addChatMessage("assistant", "📄 Blueprint received. Before we generate your takeoff, let's gather a few specifications to ensure accuracy...");
    
    // Store file and show questionnaire
    setPendingFile(file);
    setPendingFilename(file.name);
    setShowQuestionnaire(true);
    
    // Clear file input
    e.target.value = "";
  };
  
  // Handle questionnaire completion
  const handleQuestionnaireComplete = async (answers) => {
    setShowQuestionnaire(false);
    setQuestionnaireAnswers(answers);
    
    addChatMessage("assistant", `✅ Specifications received:
• Stories: ${answers.stories}
• Ceiling Height: ${answers.ceilingHeight} ft
• Exterior Walls: ${answers.exteriorWallType}
• Roof Pitch: ${answers.roofPitch || '4/12'}
• Finish Level: ${answers.finishLevel}

🔍 Now analyzing blueprint with your specifications...`);
    
    // Proceed with analysis
    await processBlueprint(pendingFile, answers);
    setPendingFile(null);
  };
  
  // Handle questionnaire skip
  const handleQuestionnaireSkip = async () => {
    setShowQuestionnaire(false);
    
    addChatMessage("assistant", "⚠️ Using default specifications. For more accurate estimates, consider completing the questionnaire next time.\n\n🔍 Analyzing blueprint...");
    
    // Proceed with analysis using defaults
    await processBlueprint(pendingFile, null);
    setPendingFile(null);
  };
  
  // Handle questionnaire close
  const handleQuestionnaireClose = () => {
    setShowQuestionnaire(false);
    setPendingFile(null);
    setPendingFilename("");
    addChatMessage("assistant", "Blueprint analysis cancelled. Upload another blueprint when ready.");
  };
  
  // Process blueprint with questionnaire answers
  const processBlueprint = async (file, answers) => {
    if (!file) return;
    
    setAnalyzing(true);
    setUploading(true);
    
    // Add progress message immediately
    addChatMessage("assistant", `🔵 **Comprehensive Blueprint Analysis Started**

This takes **3-7 minutes** for complete material extraction.

**Processing:**
• 📐 Structural elements & framing
• ⚡ Electrical components (outlets, switches, fixtures)
• 🚿 Plumbing fixtures & piping
• 🏠 Exterior materials (roofing, siding, windows)
• 🎨 Finishes, trim & paint

**Your Specifications:**
• ${answers?.stories || '1'} story | ${answers?.ceilingHeight || '9'}ft ceilings
• ${answers?.roofType?.replace('_', ' ') || 'Asphalt Shingles'} roof at ${answers?.roofPitch || '4/12'} pitch
• ${answers?.finishLevel || 'Standard'} finish level

⏳ Please wait while AI analyzes every detail...`);
    
    try {
      const formData = new FormData();
      formData.append("file", file);
      
      // Build instruction string with questionnaire answers
      let instruction = "Full architectural and MEP takeoff with detailed quantities";
      if (answers) {
        instruction += `\n\nPROJECT SPECIFICATIONS FROM PRE-ANALYSIS QUESTIONNAIRE:
- Number of Stories: ${answers.stories}
- Ceiling Height: ${answers.ceilingHeight} ft
- Exterior Wall Framing: ${answers.exteriorWallType}
- Interior Wall Framing: ${answers.interiorWallType}
- Finish Level: ${answers.finishLevel}
- Electrical Scope: ${answers.electricalScope}
- Plumbing Scope: ${answers.plumbingScope}
- HVAC Scope: ${answers.hvacScope}
- Garage: ${answers.garageSize}
- Foundation Type: ${answers.hasBasement}
- Roof Type: ${answers.roofType}
- Roof Pitch: ${answers.roofPitch || '4/12'}
- Additional Notes: ${answers.additionalNotes || 'None'}

Use these specifications to adjust quantities and exclude items marked as "by_others".`;
      }
      
      formData.append("instruction", instruction);
      
      // If we have answers, send them as metadata
      if (answers) {
        formData.append("questionnaire_answers", JSON.stringify(answers));
      }
      
      const response = await API.post("/blueprint-takeoff/analyze", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 600000 // 10 minute timeout for comprehensive analysis
      });
      
      const { matched_items, statistics: stats, chat_messages } = response.data;
      
      // Apply questionnaire-based adjustments to materials
      let adjustedItems = matched_items || [];
      if (answers) {
        adjustedItems = applyQuestionnaireAdjustments(adjustedItems, answers);
      }
      
      // Update materials and stats
      setMaterials(adjustedItems);
      setStatistics(stats || null);
      
      // Track gaps for database improvement
      if (adjustedItems && adjustedItems.length > 0) {
        trackGaps(adjustedItems);
      }
      
      // Add chat messages from backend
      if (chat_messages && chat_messages.length > 0) {
        chat_messages.forEach(msg => {
          addChatMessage(msg.role, msg.content);
        });
      }
      
      toast.success(`Analysis complete: ${adjustedItems?.length || 0} items matched`);
      
    } catch (err) {
      console.error("Blueprint analysis error:", err);
      addChatMessage("assistant", `⚠️ Analysis error: ${err.response?.data?.detail || err.message}`);
      toast.error(err.response?.data?.detail || "Blueprint analysis failed");
    } finally {
      setUploading(false);
      setAnalyzing(false);
    }
  };
  
  // Apply questionnaire-based adjustments to materials
  const applyQuestionnaireAdjustments = (items, answers) => {
    if (!answers) return items;
    
    let adjustedItems = [...items];
    
    // Filter out items based on MEP scope
    if (answers.electricalScope === "by_others") {
      adjustedItems = adjustedItems.filter(item => 
        item.category?.toLowerCase() !== "electrical"
      );
    }
    if (answers.plumbingScope === "by_others") {
      adjustedItems = adjustedItems.filter(item => 
        item.category?.toLowerCase() !== "plumbing"
      );
    }
    if (answers.hvacScope === "by_others") {
      adjustedItems = adjustedItems.filter(item => 
        item.category?.toLowerCase() !== "hvac"
      );
    }
    
    // Adjust for ceiling height
    if (answers.ceilingHeight && answers.ceilingHeight !== "8") {
      const heightFactor = parseFloat(answers.ceilingHeight) / 8;
      
      adjustedItems = adjustedItems.map(item => {
        // Adjust wall-related items for taller ceilings
        const heightCategories = ['drywall', 'paint', 'trim'];
        if (heightCategories.some(cat => item.category?.toLowerCase().includes(cat)) &&
            item.item_name?.toLowerCase().includes('wall')) {
          return {
            ...item,
            quantity: Math.ceil(item.quantity * heightFactor),
            line_total: Math.ceil(item.quantity * heightFactor) * (item.unit_cost || 0)
          };
        }
        return item;
      });
    }
    
    // Adjust finish level pricing
    const finishMultipliers = {
      builder: 0.75,
      standard: 1.0,
      upgraded: 1.35,
      custom: 1.75
    };
    const finishMultiplier = finishMultipliers[answers.finishLevel] || 1.0;
    
    // Apply finish level to finish-related items
    const finishCategories = ['flooring', 'cabinets', 'plumbing', 'electrical', 'paint', 'trim'];
    adjustedItems = adjustedItems.map(item => {
      if (finishCategories.some(cat => item.category?.toLowerCase().includes(cat))) {
        const adjustedCost = (item.unit_cost || 0) * finishMultiplier;
        return {
          ...item,
          unit_cost: adjustedCost,
          line_total: (item.quantity || 0) * adjustedCost
        };
      }
      return item;
    });
    
    return adjustedItems;
  };
  
  const addChatMessage = (role, content) => {
    setChatHistory(prev => [...prev, {
      role,
      content,
      timestamp: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
    }]);
  };
  
  // ═══════════════════════════════════════════════════════════════════════════
  // CHAT INTERACTION
  // ═══════════════════════════════════════════════════════════════════════════
  
  const handleChatSubmit = async () => {
    if (!chatInput.trim() || aiLoading) return;
    
    const message = chatInput.trim();
    setChatInput("");
    addChatMessage("user", message);
    setAiLoading(true);
    
    try {
      // For now, provide helpful responses about the takeoff
      const lowerMsg = message.toLowerCase();
      
      if (lowerMsg.includes("review") || lowerMsg.includes("flag")) {
        const reviewItems = materials.filter(m => m.needs_review);
        addChatMessage("assistant", `📋 Items needing review: ${reviewItems.length}\n\n${reviewItems.map(i => `• ${i.item_name} (${i.match_score})`).join('\n')}`);
      } else if (lowerMsg.includes("total") || lowerMsg.includes("cost")) {
        const total = materials.reduce((sum, m) => sum + (m.line_total || 0), 0);
        addChatMessage("assistant", `💰 Current Total: $${total.toLocaleString()}\n\nMaterials: $${statistics?.materials_total?.toLocaleString() || 0}\nLabor Est: $${statistics?.labor_estimate?.toLocaleString() || 0}`);
      } else if (lowerMsg.includes("category") || lowerMsg.includes("breakdown")) {
        const categories = [...new Set(materials.map(m => m.category))];
        addChatMessage("assistant", `📊 Categories in takeoff:\n\n${categories.map(c => `• ${c}: ${materials.filter(m => m.category === c).length} items`).join('\n')}`);
      } else {
        addChatMessage("assistant", "I can help you with:\n\n• Show items needing review\n• Get total cost breakdown\n• Show category breakdown\n• Export or generate estimate\n\nWhat would you like to know?");
      }
    } catch (err) {
      addChatMessage("assistant", "⚠️ Error processing request. Please try again.");
    } finally {
      setAiLoading(false);
    }
  };
  
  // ═══════════════════════════════════════════════════════════════════════════
  // MATERIAL EDITING
  // ═══════════════════════════════════════════════════════════════════════════
  
  const handleMaterialChange = (index, field, value) => {
    setMaterials(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      
      // Recalculate line total
      const qty = parseFloat(updated[index].quantity) || 0;
      const cost = parseFloat(updated[index].unit_cost) || 0;
      updated[index].line_total = qty * cost;
      
      return updated;
    });
  };
  
  // ═══════════════════════════════════════════════════════════════════════════
  // EXPORT & GENERATE
  // ═══════════════════════════════════════════════════════════════════════════
  
  const handleExportCSV = () => {
    if (materials.length === 0) {
      toast.error("No data to export");
      return;
    }
    
    const headers = ["Category", "Item", "Qty", "Unit", "Unit Cost", "Source", "Line Total"];
    const rows = materials.map(m => [
      m.category || "",
      m.item_name || "",
      m.quantity || 0,
      m.unit || "EA",
      (m.unit_cost || 0).toFixed(2),
      m.source || "",
      (m.line_total || 0).toFixed(2)
    ]);
    
    const csv = [headers.join(","), ...rows.map(r => r.map(c => `"${c}"`).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `blueprint_takeoff_${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("CSV exported");
  };
  
  const handlePrint = () => {
    if (materials.length === 0) {
      toast.error("No data to print");
      return;
    }
    
    const total = materials.reduce((sum, m) => sum + (m.line_total || 0), 0);
    
    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Blueprint Takeoff</title>
          <style>
            body { font-family: 'Courier New', monospace; padding: 20px; background: #0a0e1a; color: #e2e8f0; }
            h1 { color: #10b981; font-size: 18px; margin-bottom: 4px; }
            .subtitle { color: #64748b; font-size: 11px; margin-bottom: 20px; }
            table { width: 100%; border-collapse: collapse; font-size: 10px; }
            th { background: #1e293b; color: #10b981; padding: 8px; text-align: left; border: 1px solid #334155; }
            td { padding: 8px; border: 1px solid #334155; }
            .total { background: #1e293b; font-weight: bold; }
            .source-good { color: #10b981; }
            .source-fair { color: #eab308; }
            .source-review { color: #ef4444; }
            @media print { body { background: white; color: black; } th { background: #f1f5f9; color: black; } }
          </style>
        </head>
        <body>
          <h1>BLUEPRINT TAKEOFF - MATERIAL ANALYSIS</h1>
          <p class="subtitle">Generated: ${new Date().toLocaleString()} | Items: ${materials.length}</p>
          <table>
            <thead>
              <tr>
                <th>Category</th>
                <th>Item</th>
                <th>Qty</th>
                <th>Unit</th>
                <th>Unit Cost</th>
                <th>Source</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              ${materials.map(m => `
                <tr>
                  <td>${m.category || ''}</td>
                  <td>${m.item_name || ''}</td>
                  <td>${m.quantity || 0}</td>
                  <td>${m.unit || 'EA'}</td>
                  <td>$${(m.unit_cost || 0).toFixed(2)}</td>
                  <td class="${m.match_score >= 0.90 ? 'source-good' : m.match_score >= 0.80 ? 'source-fair' : 'source-review'}">${m.source || ''}</td>
                  <td>$${(m.line_total || 0).toFixed(2)}</td>
                </tr>
              `).join('')}
              <tr class="total">
                <td colspan="6">TOTAL</td>
                <td>$${total.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
              </tr>
            </tbody>
          </table>
        </body>
      </html>
    `;
    
    const printWindow = window.open("", "_blank");
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => printWindow.print(), 300);
  };
  
  const handleGenerateEstimate = async () => {
    if (materials.length === 0) {
      toast.error("No materials to generate estimate");
      return;
    }
    
    if (!selectedTarget) {
      toast.warning("Please select a target client/lead");
      return;
    }
    
    const targetInfo = targets.find(t => t.id === selectedTarget);
    setGeneratingEstimate(true);
    
    try {
      const today = new Date().toISOString().split("T")[0];
      const estimateName = targetInfo?.name 
        ? `${targetInfo.name} - Blueprint Takeoff ${today}`
        : `Blueprint Takeoff - ${today}`;
      
      const createPayload = {
        project_description: estimateName,
        job_name: estimateName,
        client_name: targetInfo?.name || null,
      };
      
      if (targetInfo?.type === "lead") {
        createPayload.lead_id = selectedTarget;
      } else if (targetInfo?.type === "client") {
        createPayload.client_id = selectedTarget;
      }
      
      const createRes = await API.post("/estimates", createPayload);
      
      if (!createRes.data?.estimate_id) {
        throw new Error("Failed to create estimate");
      }
      
      const estimateId = createRes.data.estimate_id;
      
      // Save materials
      const materialsPayload = materials.map(m => ({
        item_name: m.item_name || "Unnamed Item",
        quantity: Number(m.quantity) || 0,
        unit: m.unit || "EA",
        unit_cost: Number(m.unit_cost) || 0,
        markup_percent: 25,
        source: m.source || "",
        match_score: m.match_score || 0,
      }));
      
      await API.post(`/estimates/${estimateId}/materials/bulk`, { materials: materialsPayload });
      
      toast.success("Estimate created! Redirecting...");
      addChatMessage("assistant", `✅ Estimate created for ${targetInfo?.name}. Redirecting to Mission Scope...`);
      
      setTimeout(() => {
        navigate("/estimates", { state: { newEstimateId: estimateId } });
      }, 1500);
      
    } catch (err) {
      console.error("Generate estimate error:", err);
      toast.error(err.response?.data?.detail || "Failed to generate estimate");
    } finally {
      setGeneratingEstimate(false);
    }
  };
  
  // ═══════════════════════════════════════════════════════════════════════════
  // FILTERED MATERIALS
  // ═══════════════════════════════════════════════════════════════════════════
  
  const filteredMaterials = materials.filter(m => {
    if (activeFilter === "matched") return m.match_score >= 0.80;
    if (activeFilter === "review") return m.match_score < 0.80 || m.needs_review;
    return true;
  });
  
  const matchedCount = materials.filter(m => m.match_score >= 0.80).length;
  const reviewCount = materials.filter(m => m.match_score < 0.80 || m.needs_review).length;
  
  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════════════════
  
  return (
    <div className="min-h-screen bg-[#0a0e1a] text-white flex flex-col relative">
      {/* ═══════════════════════════════════════════════════════════════════════
          HEADER
          ═══════════════════════════════════════════════════════════════════════ */}
      <header className="border-b border-slate-800/50 px-6 py-4 flex items-center justify-between bg-gradient-to-r from-[#0a0e1a] to-[#0f172a]">
        <div>
          <h1 className="text-lg font-mono font-bold text-emerald-400 tracking-wide">
            BLUEPRINT ANALYSIS COMMAND
          </h1>
          <p className="text-xs text-slate-500 font-mono mt-0.5">
            Upload → Analyze → Match → Build Estimate
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            disabled={uploading}
            onClick={handleUploadClick}
            className="border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10 hover:text-emerald-300 font-mono"
          >
            {uploading ? (
              <>
                <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" /> PROCESSING...
              </>
            ) : (
              <>
                <FileText className="h-3.5 w-3.5 mr-2" /> UPLOAD BLUEPRINT PDF
              </>
            )}
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf"
            className="hidden"
            onChange={handleFileChange}
          />
        </div>
      </header>
      
      {/* ═══════════════════════════════════════════════════════════════════════
          MAIN CONTENT
          ═══════════════════════════════════════════════════════════════════════ */}
      <main className={`flex-1 overflow-hidden flex flex-col p-4 gap-4 transition-all duration-300 ${aiPanelOpen ? 'mr-[450px]' : ''}`}>
        
        {/* BLUEPRINT VIEWER */}
        <div className="flex-shrink-0 bg-[#0f172a] border border-slate-800/50 rounded-lg overflow-hidden">
          <div className="px-4 py-2 border-b border-slate-800/50 flex items-center justify-between">
            <span className="text-xs font-mono text-slate-400 uppercase tracking-wider">
              Blueprint Viewer
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setPdfScale(s => Math.max(0.5, s - 0.25))}
                className="h-7 w-7 p-0 text-slate-400 hover:text-white"
              >
                <ZoomOut className="h-3.5 w-3.5" />
              </Button>
              <span className="text-xs text-slate-500 font-mono w-12 text-center">{Math.round(pdfScale * 100)}%</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setPdfScale(s => Math.min(2, s + 0.25))}
                className="h-7 w-7 p-0 text-slate-400 hover:text-white"
              >
                <ZoomIn className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
          <div className="h-[350px] flex items-center justify-center bg-[#080c14]" style={{ transform: `scale(${pdfScale})`, transformOrigin: 'center center' }}>
            {!blueprintUrl ? (
              <div className="text-center px-8">
                <FileText className="h-12 w-12 text-slate-700 mx-auto mb-3" />
                <p className="text-sm text-slate-500 font-mono">
                  Upload a blueprint PDF to view here
                </p>
                <p className="text-xs text-slate-600 mt-1">
                  Supports multi-page PDFs up to 50MB
                </p>
              </div>
            ) : (
              <iframe
                src={blueprintUrl}
                title="Blueprint PDF"
                className="w-full h-full"
                style={{ minHeight: '350px' }}
              />
            )}
          </div>
        </div>
        
        {/* ACTION TOOLBAR */}
        <div className="flex items-center justify-between px-1">
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-500 font-mono uppercase tracking-wider mr-2">Export</span>
            <Button
              variant="outline"
              size="sm"
              onClick={handleExportCSV}
              className="border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white font-mono text-xs"
            >
              <Table2 className="h-3.5 w-3.5 mr-1.5" />
              CSV
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handlePrint}
              className="border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white font-mono text-xs"
            >
              <Printer className="h-3.5 w-3.5 mr-1.5" />
              Print
            </Button>
          </div>
          
          <div className="flex items-center gap-3">
            <Select value={selectedTarget} onValueChange={setSelectedTarget}>
              <SelectTrigger className="w-56 bg-slate-800/50 border-slate-700 text-white font-mono text-xs focus:border-emerald-500">
                <div className="flex items-center gap-2">
                  <Users className="h-3.5 w-3.5 text-slate-400" />
                  <SelectValue placeholder="Select Target..." />
                </div>
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700 font-mono">
                {loadingTargets ? (
                  <div className="py-2 px-3 text-xs text-slate-400">Loading...</div>
                ) : targets.length === 0 ? (
                  <div className="py-2 px-3 text-xs text-slate-400">No targets found</div>
                ) : (
                  targets.map(t => (
                    <SelectItem key={t.id} value={t.id} className="text-white text-xs">
                      {t.name}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
            
            <Button
              size="sm"
              onClick={handleGenerateEstimate}
              disabled={generatingEstimate || materials.length === 0}
              className="bg-emerald-600 hover:bg-emerald-500 text-white font-mono text-xs shadow-lg shadow-emerald-900/30"
            >
              {generatingEstimate ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                  GENERATING...
                </>
              ) : (
                <>
                  <Sparkles className="h-3.5 w-3.5 mr-1.5" />
                  GENERATE ESTIMATE
                </>
              )}
            </Button>
          </div>
        </div>
        
        {/* MATERIAL TABLE */}
        <div className="flex-1 bg-[#0f172a] border border-slate-800/50 rounded-lg overflow-hidden flex flex-col">
          {/* Table Header */}
          <div className="px-4 py-3 border-b border-slate-800/50 flex items-center justify-between">
            <div className="flex items-center gap-1">
              <span className="text-xs font-mono text-emerald-400 uppercase tracking-wider">
                Material Takeoff
              </span>
              {statistics && (
                <span className="text-xs text-slate-500 font-mono ml-2">
                  — {statistics.match_rate}% matched to database
                </span>
              )}
            </div>
            
            {/* Filter Tabs */}
            <div className="flex items-center gap-1 bg-slate-800/50 rounded-lg p-0.5">
              <button
                onClick={() => setActiveFilter("all")}
                className={`px-3 py-1 text-xs font-mono rounded transition-colors ${
                  activeFilter === "all" 
                    ? "bg-slate-700 text-white" 
                    : "text-slate-400 hover:text-white"
                }`}
              >
                All ({materials.length})
              </button>
              <button
                onClick={() => setActiveFilter("matched")}
                className={`px-3 py-1 text-xs font-mono rounded transition-colors flex items-center gap-1 ${
                  activeFilter === "matched" 
                    ? "bg-emerald-500/20 text-emerald-400" 
                    : "text-slate-400 hover:text-emerald-400"
                }`}
              >
                <Check className="h-3 w-3" />
                Matched ({matchedCount})
              </button>
              <button
                onClick={() => setActiveFilter("review")}
                className={`px-3 py-1 text-xs font-mono rounded transition-colors flex items-center gap-1 ${
                  activeFilter === "review" 
                    ? "bg-amber-500/20 text-amber-400" 
                    : "text-slate-400 hover:text-amber-400"
                }`}
              >
                <AlertTriangle className="h-3 w-3" />
                Review ({reviewCount})
              </button>
            </div>
          </div>
          
          {/* Table Content */}
          <div className="flex-1 overflow-auto takeoff-scrollbar">
            <Table>
              <TableHeader className="sticky top-0 z-10">
                <TableRow className="bg-slate-900 border-b border-slate-800">
                  <TableHead className="text-slate-400 text-xs font-mono w-24">Category</TableHead>
                  <TableHead className="text-slate-400 text-xs font-mono">Item Name</TableHead>
                  <TableHead className="text-slate-400 text-xs font-mono w-20 text-right">Qty</TableHead>
                  <TableHead className="text-slate-400 text-xs font-mono w-16">Unit</TableHead>
                  <TableHead className="text-slate-400 text-xs font-mono w-24 text-right">Unit Cost</TableHead>
                  <TableHead className="text-slate-400 text-xs font-mono w-32">Source</TableHead>
                  <TableHead className="text-slate-400 text-xs font-mono w-28 text-right">Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredMaterials.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-12 text-slate-500">
                      {materials.length === 0 
                        ? "Upload a blueprint to generate material takeoff"
                        : "No items match current filter"}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredMaterials.map((m, idx) => (
                    <TableRow 
                      key={idx} 
                      className={`border-b border-slate-800/50 hover:bg-slate-800/30 ${
                        m.needs_review || m.match_score < 0.80 ? 'bg-amber-500/5' : ''
                      }`}
                    >
                      <TableCell className="text-xs text-slate-400 font-mono">
                        {m.category || '—'}
                      </TableCell>
                      <TableCell>
                        <div className="text-xs text-white font-medium">{m.item_name}</div>
                        {m.description && (
                          <div className="text-xs text-slate-500 mt-0.5">{m.description}</div>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <Input
                          type="number"
                          value={m.quantity || ""}
                          onChange={(e) => handleMaterialChange(idx, "quantity", e.target.value)}
                          className="w-16 h-7 bg-slate-800/50 border-slate-700 text-white text-xs text-right font-mono"
                        />
                      </TableCell>
                      <TableCell className="text-xs text-slate-400 font-mono">
                        {m.unit || "EA"}
                      </TableCell>
                      <TableCell className="text-right">
                        <Input
                          type="number"
                          step="0.01"
                          value={m.unit_cost || ""}
                          onChange={(e) => handleMaterialChange(idx, "unit_cost", e.target.value)}
                          className="w-20 h-7 bg-slate-800/50 border-slate-700 text-white text-xs text-right font-mono"
                        />
                      </TableCell>
                      <TableCell>
                        <MatchBadge score={m.match_score} source={m.source} />
                      </TableCell>
                      <TableCell className="text-right text-xs font-mono font-semibold text-emerald-400">
                        ${(m.line_total || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </TableCell>
                    </TableRow>
                  ))
                )}
                
                {/* Totals Row */}
                {materials.length > 0 && (
                  <TableRow className="bg-slate-900/80 border-t-2 border-emerald-500/30">
                    <TableCell colSpan={6} className="text-xs font-mono font-bold text-white">
                      TOTAL ({materials.length} items)
                    </TableCell>
                    <TableCell className="text-right text-sm font-mono font-bold text-emerald-400">
                      ${materials.reduce((sum, m) => sum + (m.line_total || 0), 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </div>
        
        {/* SUMMARY BAR */}
        {statistics && (
          <div className="flex items-center justify-between px-4 py-2 bg-slate-800/30 rounded-lg border border-slate-800/50">
            <div className="flex items-center gap-6 text-xs font-mono">
              <div>
                <span className="text-slate-500">Materials:</span>
                <span className="text-white ml-2">${statistics.materials_total?.toLocaleString() || 0}</span>
              </div>
              <div>
                <span className="text-slate-500">Labor Est:</span>
                <span className="text-white ml-2">${statistics.labor_estimate?.toLocaleString() || 0}</span>
              </div>
            </div>
            <div className="text-sm font-mono font-bold">
              <span className="text-slate-400">Total:</span>
              <span className="text-emerald-400 ml-2">${statistics.grand_total?.toLocaleString() || 0}</span>
            </div>
          </div>
        )}
      </main>
      
      {/* ═══════════════════════════════════════════════════════════════════════
          AI COMMAND TOGGLE BUTTON
          ═══════════════════════════════════════════════════════════════════════ */}
      <button
        onClick={() => setAiPanelOpen(!aiPanelOpen)}
        className={`fixed top-[180px] right-5 z-40 px-3 py-2 rounded-lg border-2 font-mono text-center transition-all duration-200 ${
          aiPanelOpen
            ? 'bg-gradient-to-br from-emerald-600 to-emerald-700 border-emerald-400 shadow-lg shadow-emerald-500/30'
            : 'bg-gradient-to-br from-slate-800 to-slate-900 border-emerald-500/50 hover:border-emerald-400 hover:shadow-lg hover:shadow-emerald-500/20'
        }`}
        style={{ display: aiPanelOpen ? 'none' : 'block' }}
      >
        <div className={`text-[10px] uppercase tracking-wider ${aiPanelOpen ? 'text-white' : 'text-emerald-400'}`}>
          AI Command
        </div>
        <div className={`text-[9px] uppercase tracking-wider ${aiPanelOpen ? 'text-emerald-200' : 'text-slate-500'}`}>
          {aiPanelOpen ? 'Active' : 'Standby'}
        </div>
      </button>
      
      {/* ═══════════════════════════════════════════════════════════════════════
          AI COMMAND PANEL (Sliding)
          ═══════════════════════════════════════════════════════════════════════ */}
      <div 
        className={`fixed top-0 right-0 h-full w-[450px] bg-[#18181b] border-l-2 border-emerald-500/50 shadow-2xl z-50 flex flex-col transition-transform duration-300 ${
          aiPanelOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Panel Header */}
        <div className="px-4 py-3 bg-[#0f172a] border-b border-emerald-500/30 flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center shadow-lg shadow-emerald-500/30">
            <span className="text-white font-mono font-bold text-xs">AI</span>
          </div>
          <div className="flex-1">
            <h3 className="text-xs font-mono font-bold text-emerald-400 uppercase tracking-wider">
              Command Interface
            </h3>
            <p className="text-[10px] font-mono text-slate-500 uppercase tracking-wider">
              Neural Analysis System
            </p>
          </div>
          <button
            onClick={() => setAiPanelOpen(false)}
            className="w-8 h-8 rounded-lg bg-slate-800/50 flex items-center justify-center text-slate-400 hover:text-white hover:bg-slate-700 transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        
        {/* Chat History */}
        <div 
          ref={chatContainerRef}
          className="flex-1 overflow-y-auto p-4 space-y-3 takeoff-scrollbar"
        >
          {chatHistory.map((msg, idx) => (
            <ChatMessage key={idx} message={msg} />
          ))}
          
          {(aiLoading || analyzing) && (
            <div className="flex items-center gap-2 text-xs text-emerald-400 font-mono">
              <Loader2 className="h-3 w-3 animate-spin" />
              <span>{analyzing ? 'Analyzing blueprint...' : 'Processing...'}</span>
            </div>
          )}
        </div>
        
        {/* Chat Input */}
        <div className="p-4 border-t border-slate-800">
          <div className="flex items-center gap-2">
            <Input
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleChatSubmit()}
              placeholder="Ask about the takeoff..."
              className="flex-1 bg-slate-800/50 border-slate-700 text-white font-mono text-xs placeholder:text-slate-500 focus:border-emerald-500"
              disabled={aiLoading}
            />
            <Button
              onClick={handleChatSubmit}
              disabled={aiLoading || !chatInput.trim()}
              className="bg-emerald-600 hover:bg-emerald-500 w-9 h-9 p-0"
            >
              <Send className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
      
      {/* Database Quality Dashboard - Toggle with Ctrl+Shift+D */}
      {showDevTools && (
        <DatabaseQualityDashboard 
          materials={materials}
          onExport={(gaps) => {
            console.log(`Exported ${gaps.length} gaps for review`);
            toast.success(`Exported ${gaps.length} items needing attention`);
          }}
        />
      )}
      
      {/* Pre-Analysis Questionnaire Modal */}
      {showQuestionnaire && (
        <PreAnalysisQuestionnaire
          blueprintFilename={pendingFilename}
          onComplete={handleQuestionnaireComplete}
          onSkip={handleQuestionnaireSkip}
          onClose={handleQuestionnaireClose}
        />
      )}
      
      {/* Custom Scrollbar Styles - add to index.css instead */}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// MATCH BADGE COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

const MatchBadge = ({ score, source }) => {
  if (score >= 0.90) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-mono font-semibold bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
        <Check className="h-2.5 w-2.5" />
        DB Match ({score.toFixed(2)})
      </span>
    );
  }
  
  if (score >= 0.80) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-mono font-semibold bg-yellow-500/20 text-yellow-400 border border-yellow-500/30">
        <AlertTriangle className="h-2.5 w-2.5" />
        Fair ({score.toFixed(2)})
      </span>
    );
  }
  
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-mono font-semibold bg-red-500/20 text-red-400 border border-red-500/30">
      <HelpCircle className="h-2.5 w-2.5" />
      REVIEW ({score.toFixed(2)})
    </span>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// CHAT MESSAGE COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

const ChatMessage = ({ message }) => {
  const isUser = message.role === "user";
  
  return (
    <div className={`${isUser ? 'ml-8' : 'mr-4'}`}>
      <div className={`rounded-lg px-3 py-2 ${
        isUser 
          ? 'bg-blue-900/30 border border-blue-500/30' 
          : 'bg-slate-800/50 border border-slate-700/50'
      }`}>
        <div className={`text-[10px] font-mono uppercase tracking-wider mb-1 ${
          isUser ? 'text-blue-400' : 'text-emerald-400'
        }`}>
          {isUser ? 'You' : 'AI Command'}
        </div>
        <div className="text-xs text-slate-200 whitespace-pre-wrap font-mono leading-relaxed">
          {message.content}
        </div>
        {message.timestamp && (
          <div className="text-[9px] text-slate-600 mt-1 font-mono">
            {message.timestamp}
          </div>
        )}
      </div>
    </div>
  );
};

export default BlueprintTakeoffPage;
